import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { initAnalytics, applyConsent } from '../lib/analytics';
import { privacyAPI } from '../lib/api';

const STORAGE_KEY = 'riq_cookie_consent';
const VISITOR_KEY = 'riq_visitor_id';

const getVisitorId = () => {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = (crypto?.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
};

const CATEGORIES = [
  { key: 'necessary', label: 'Strictly Necessary', desc: 'Required for the site to function (login, security, your preferences). Always on.', locked: true },
  { key: 'analytics', label: 'Analytics', desc: 'Helps us understand how visitors use the site so we can improve it (e.g. Google Analytics).' },
  { key: 'marketing', label: 'Marketing', desc: 'Used to measure campaigns and show relevant ads (e.g. Meta Pixel).' },
];

const COMPACT_PATHS = ['/schedule/', '/ai-interview/', '/shared/ai-report/', '/join/', '/interview-room/'];
const smallBtn = { background: 'transparent', color: '#E0F2FE', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 8, padding: '0.3rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' };

// Slim in-flow strip for public candidate pages so it never covers page controls.
const CompactBar = ({ onAccept, onReject, onCustomize }) => (
  <div data-testid="cookie-consent-compact" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem', flexWrap: 'wrap', padding: '0.5rem 1rem', maxWidth: 1180, margin: '0 auto' }}>
    <p style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.4, color: '#E0F2FE', flex: '1 1 200px' }}>
      Essential cookies only, unless you allow more. <a href="/legal/privacy" style={{ color: '#fff', textDecoration: 'underline' }}>Privacy</a>
    </p>
    <div style={{ display: 'flex', gap: '0.4rem' }}>
      <button data-testid="cookie-customize-btn" onClick={onCustomize} style={smallBtn}>Customize</button>
      <button data-testid="cookie-reject-btn" onClick={onReject} style={smallBtn}>Reject</button>
      <button data-testid="cookie-accept-btn" onClick={onAccept} style={{ ...smallBtn, background: '#fff', color: '#0076A3', border: 'none', fontWeight: 700 }}>Accept</button>
    </div>
  </div>
);

const Toggle = ({ on, locked, onChange, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => !locked && onChange(!on)}
    aria-pressed={on}
    disabled={locked}
    style={{
      width: 42, height: 24, borderRadius: 999, border: 'none', position: 'relative', flexShrink: 0,
      cursor: locked ? 'not-allowed' : 'pointer',
      background: on ? '#00A0E0' : '#475569', opacity: locked ? 0.7 : 1,
      transition: 'background-color 0.2s ease',
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: on ? 21 : 3, width: 18, height: 18, borderRadius: 999,
      background: '#fff', transition: 'left 0.2s ease',
    }} />
  </button>
);

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });
  const { pathname } = useLocation();
  const compact = COMPACT_PATHS.some((p) => pathname.startsWith(p));

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setVisible(true);
    else initAnalytics();
    const reopen = () => { setCustomizing(false); setVisible(true); };
    window.addEventListener('riq:open-cookie-settings', reopen);
    return () => window.removeEventListener('riq:open-cookie-settings', reopen);
  }, []);

  const persist = (consent) => {
    const value = { ...consent, necessary: true, at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    setVisible(false);
    setCustomizing(false);
    applyConsent(value);
    // Record the choice server-side for auditability (fire-and-forget).
    privacyAPI.logConsent({
      visitor_id: getVisitorId(),
      analytics: !!value.analytics,
      marketing: !!value.marketing,
      choice: value.choice,
    }).catch(() => {});
  };

  if (!visible) return null;

  const acceptAll = () => persist({ analytics: true, marketing: true, choice: 'all' });
  const rejectAll = () => persist({ analytics: false, marketing: false, choice: 'essential_only' });
  const savePrefs = () => persist({ analytics: prefs.analytics, marketing: prefs.marketing, choice: 'custom' });

  return (
    <div
      data-testid="cookie-consent-banner"
      style={compact
        ? { position: 'relative', zIndex: 50, background: '#0076A3', color: '#fff' }
        : { position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, background: '#0076A3', color: '#fff', boxShadow: '0 -4px 24px rgba(0,0,0,0.18)' }}
    >
      {compact && !customizing ? <CompactBar onAccept={acceptAll} onReject={rejectAll} onCustomize={() => setCustomizing(true)} /> : (
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '1.1rem 1.25rem' }}>
        {!customizing ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.25rem', flexWrap: 'wrap' }}>
            <p style={{ fontSize: '0.8125rem', lineHeight: 1.6, margin: 0, color: '#CBD5E1', flex: '1 1 380px' }}>
              We use cookies to operate this site, remember your preferences, and analyse usage. Essential cookies are
              always on. Choose which optional cookies to allow. See our{' '}
              <a href="/legal/privacy" style={{ color: '#7FB0FF', textDecoration: 'underline' }}>Privacy Policy</a>.
            </p>
            <div style={{ display: 'flex', gap: '0.625rem', flex: '0 1 auto', minWidth: 0, flexWrap: 'wrap' }}>
              <button data-testid="cookie-customize-btn" onClick={() => setCustomizing(true)}
                style={{ background: 'transparent', color: '#CBD5E1', border: '1px solid #3A465E', borderRadius: 10, padding: '0.5rem 1.1rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
                Customize
              </button>
              <button data-testid="cookie-reject-btn" onClick={rejectAll}
                style={{ background: 'transparent', color: '#CBD5E1', border: '1px solid #3A465E', borderRadius: 10, padding: '0.5rem 1.1rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
                Reject non-essential
              </button>
              <button data-testid="cookie-accept-btn" onClick={acceptAll}
                style={{ background: '#00A0E0', color: '#141617', border: 'none', borderRadius: 4, padding: '0.5rem 1.4rem', fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer' }}>
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="cookie-customize-panel">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
              <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 700 }}>Cookie Preferences</span>
              <a href="/legal/privacy" style={{ color: '#7FB0FF', fontSize: '0.75rem', textDecoration: 'underline' }}>Privacy Policy</a>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.625rem', maxHeight: '36vh', overflowY: 'auto', marginBottom: '1rem' }}>
              {CATEGORIES.map((c) => (
                <div key={c.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem', padding: '0.75rem 0.875rem', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid #2A3550' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#fff' }}>{c.label}{c.locked && <span style={{ color: '#7FB0FF', fontWeight: 500 }}> · Always on</span>}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: 2, lineHeight: 1.5 }}>{c.desc}</div>
                  </div>
                  <Toggle
                    testid={`cookie-toggle-${c.key}`}
                    on={c.locked ? true : prefs[c.key]}
                    locked={c.locked}
                    onChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.625rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button data-testid="cookie-back-btn" onClick={() => setCustomizing(false)}
                style={{ background: 'transparent', color: '#CBD5E1', border: '1px solid #3A465E', borderRadius: 10, padding: '0.5rem 1.1rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
                Back
              </button>
              <button data-testid="cookie-save-btn" onClick={savePrefs}
                style={{ background: '#00A0E0', color: '#141617', border: 'none', borderRadius: 4, padding: '0.5rem 1.4rem', fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer' }}>
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};
